import React, { useState, useRef, useLayoutEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown, Plane, Globe, Star } from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=80";

export const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const mounted = useRef(false);

  useLayoutEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      setIsVisible(true);
    }
  }, []);

  const scrollToDestinations = () => {
    const element = document.querySelector("#destinations");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-background"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Luxury Travel Destination"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/70 to-background/95" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/70 via-transparent to-background/70" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-1/4 left-10 w-32 h-32 border border-secondary/20 rounded-full animate-pulse opacity-30 hidden lg:block" />
      <div className="absolute bottom-1/4 right-10 w-48 h-48 border border-secondary/10 rounded-full animate-pulse opacity-20 hidden lg:block" />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div
          className={`transition-all duration-1000 ${
            isVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
        >
          {/* Tagline */}
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full border border-secondary/40 bg-secondary/10 backdrop-blur-sm">
            <Star className="w-4 h-4 text-secondary" />
            <span className="text-sm font-medium text-secondary tracking-wider uppercase">
              Premium Travel Experiences
            </span>
            <Star className="w-4 h-4 text-secondary" />
          </div>

          {/* Main Heading */}
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 leading-tight">
            <span className="text-foreground">Elevate Your</span>
            <br />
            <span className="text-gradient-gold text-shadow-gold">
              Travel Experience
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-lg sm:text-xl text-foreground/70 max-w-2xl mx-auto mb-10 leading-relaxed">
            Discover the world&apos;s most exclusive destinations with personalized
            luxury travel services. Your journey to extraordinary begins here.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button
              variant="gold"
              size="xl"
              onClick={scrollToDestinations}
              className="group"
            >
              <Plane className="w-5 h-5 mr-2 transition-transform group-hover:translate-x-1" />
              Explore Destinations
            </Button>
            <Button
              variant="luxuryOutline"
              size="xl"
              onClick={() => {
                const element = document.querySelector("#contact");
                if (element) element.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <Globe className="w-5 h-5 mr-2" />
              Plan Your Trip
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 max-w-lg mx-auto">
            {[
              { value: "50+", label: "Destinations" },
              { value: "10K+", label: "Happy Travelers" },
              { value: "15+", label: "Years Experience" },
            ].map((stat, index) => (
              <div
                key={stat.label}
                className={`text-center transition-all duration-700 delay-${
                  (index + 1) * 200
                } ${
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-5"
                }`}
              >
                <div className="text-2xl sm:text-3xl font-bold text-gradient-gold mb-1">
                  {stat.value}
                </div>
                <div className="text-xs sm:text-sm text-foreground/60 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <button
          onClick={scrollToDestinations}
          className="p-2 rounded-full border border-secondary/40 text-secondary hover:text-primary hover:border-primary transition-colors"
          aria-label="Scroll down"
        >
          <ChevronDown className="w-6 h-6" />
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
